import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Code,
  Textarea,
  Alert,
  AlertIcon,
  Divider,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { FiCheckCircle, FiFileText, FiCode, FiInfo, FiLayers, FiImage } from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import { DocumentPreview as DocumentPreviewType } from '../services/api';

interface DocumentPreviewProps {
  preview: DocumentPreviewType;
  onContinue: () => void;
  onCancel: () => void;
  isProcessing?: boolean;
}

const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  preview,
  onContinue,
  onCancel,
  isProcessing = false,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  if (!preview.success) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <VStack align="start" spacing={2}>
          <Text fontWeight="semibold">Erro na conversão do documento</Text>
          <Text fontSize="sm">{preview.error}</Text>
          <Button size="sm" onClick={onCancel} mt={2}>
            Tentar novamente
          </Button>
        </VStack>
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader>
        <HStack justify="space-between" align="center">
          <HStack>
            <FiCheckCircle color="green" size={24} />
            <VStack align="start" spacing={0}>
              <Heading size="md">Preview da Conversão</Heading>
              <Text fontSize="sm" color="gray.600">
                {preview.filename}
              </Text>
            </VStack>
          </HStack>
          <Badge colorScheme="green" variant="subtle">
            Conversão bem-sucedida
          </Badge>
        </HStack>
      </CardHeader>

      <CardBody>
        <VStack spacing={6} align="stretch">
          {/* Statistics */}
          <Box>
            <Text fontWeight="semibold" mb={3}>Estatísticas do Documento</Text>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              <Stat>
                <StatLabel>Tamanho</StatLabel>
                <StatNumber>{preview.statistics?.file_size_mb || 0} MB</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>Páginas</StatLabel>
                <StatNumber>{preview.statistics?.pages || 0}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>Chunks</StatLabel>
                <StatNumber>{preview.statistics?.chunks_count || 0}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>Caracteres</StatLabel>
                <StatNumber>{(preview.statistics?.text_length || 0).toLocaleString()}</StatNumber>
              </Stat>
              {(preview.statistics?.tables_count || 0) > 0 && (
                <Stat>
                  <StatLabel>Tabelas</StatLabel>
                  <StatNumber>{preview.statistics.tables_count}</StatNumber>
                </Stat>
              )}
              {(preview.statistics?.figures_count || 0) > 0 && (
                <Stat>
                  <StatLabel>Figuras</StatLabel>
                  <StatNumber>{preview.statistics.figures_count}</StatNumber>
                </Stat>
              )}
              {(preview.statistics?.images_count || 0) > 0 && (
                <Stat>
                  <StatLabel>Imagens</StatLabel>
                  <StatNumber>{preview.statistics.images_count}</StatNumber>
                  <StatHelpText>
                    {preview.statistics.images_with_text || 0} com texto
                    {(preview.statistics.images_with_ai_description || 0) > 0 && 
                      `, ${preview.statistics.images_with_ai_description} com IA`}
                  </StatHelpText>
                </Stat>
              )}
            </SimpleGrid>
          </Box>

          <Divider />

          {/* Preview Tabs */}
          <Tabs index={activeTab} onChange={setActiveTab} variant="enclosed">
            <TabList>
              <Tab>
                <HStack>
                  <FiFileText />
                  <Text>Markdown</Text>
                </HStack>
              </Tab>
              <Tab>
                <HStack>
                  <FiCode />
                  <Text>JSON</Text>
                </HStack>
              </Tab>
              <Tab>
                <HStack>
                  <FiInfo />
                  <Text>Metadados</Text>
                </HStack>
              </Tab>
              <Tab>
                <HStack>
                  <FiLayers />
                  <Text>Chunks ({preview.chunks?.length || 0})</Text>
                </HStack>
              </Tab>
              <Tab>
                <HStack>
                  <FiImage />
                  <Text>Imagens ({preview.images?.length || 0})</Text>
                </HStack>
              </Tab>
            </TabList>

            <TabPanels>
              {/* Markdown Tab */}
              <TabPanel>
                <Box
                  maxH="400px"
                  overflowY="auto"
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={4}
                  bg="white"
                >
                  <ReactMarkdown>{preview.markdown_content}</ReactMarkdown>
                </Box>
              </TabPanel>

              {/* JSON Tab */}
              <TabPanel>
                <Box
                  maxH="400px"
                  overflowY="auto"
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                >
                  <Code
                    display="block"
                    whiteSpace="pre-wrap"
                    p={4}
                    fontSize="sm"
                    bg="gray.50"
                  >
                    {JSON.stringify(
                      {
                        markdown_content: preview.markdown_content,
                        metadata: preview.metadata,
                        statistics: preview.statistics,
                      },
                      null,
                      2
                    )}
                  </Code>
                </Box>
              </TabPanel>

              {/* Metadata Tab */}
              <TabPanel>
                <VStack align="stretch" spacing={3}>
                  {Object.entries(preview.metadata || {}).map(([key, value]) => (
                    <HStack key={key} justify="space-between" p={3} bg="gray.50" borderRadius="md">
                      <Text fontWeight="semibold" textTransform="capitalize">
                        {key.replace('_', ' ')}:
                      </Text>
                      <Code fontSize="sm">
                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </Code>
                    </HStack>
                  ))}
                </VStack>
              </TabPanel>

              {/* Chunks Tab */}
              <TabPanel>
                <VStack align="stretch" spacing={3} maxH="400px" overflowY="auto">
                  {(preview.chunks || []).map((chunk, index) => (
                    <Box key={index} p={3} border="1px" borderColor="gray.200" borderRadius="md">
                      <HStack justify="space-between" mb={2}>
                        <Badge variant="outline" colorScheme="blue">
                          Chunk {chunk.chunk_id}
                        </Badge>
                        <Text fontSize="xs" color="gray.500">
                          {chunk.text.length} caracteres
                        </Text>
                      </HStack>
                      <Text fontSize="sm" noOfLines={3}>
                        {chunk.text}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              </TabPanel>

              {/* Images Tab */}
              <TabPanel>
                <VStack align="stretch" spacing={4} maxH="400px" overflowY="auto">
                  {(preview.images?.length || 0) === 0 ? (
                    <Text color="gray.500" textAlign="center" py={8}>
                      Nenhuma imagem encontrada no documento
                    </Text>
                  ) : (
                    (preview.images || []).map((image, index) => (
                      <Box key={index} p={4} border="1px" borderColor="gray.200" borderRadius="md">
                        <HStack justify="space-between" mb={3}>
                          <HStack>
                            <FiImage color="blue" />
                            <Text fontWeight="semibold">{image.caption}</Text>
                          </HStack>
                          <VStack align="end" spacing={0}>
                            {image.page !== undefined && (
                              <Text fontSize="xs" color="gray.500">
                                Página {image.page + 1}
                              </Text>
                            )}
                            <HStack spacing={1}>
                              <Badge 
                                colorScheme={image.extracted_text ? "green" : "gray"}
                                variant="subtle"
                              >
                                {image.extracted_text ? "Com texto" : "Sem texto"}
                              </Badge>
                              {image.ai_description && (
                                <Badge 
                                  colorScheme="purple"
                                  variant="subtle"
                                >
                                  IA
                                </Badge>
                              )}
                            </HStack>
                          </VStack>
                        </HStack>
                        
                        <VStack align="stretch" spacing={3}>
                          {image.extracted_text && (
                            <Box>
                              <Text fontSize="sm" fontWeight="semibold" mb={2} color="green.600">
                                Texto extraído (OCR):
                              </Text>
                              <Box 
                                p={3} 
                                bg="green.50" 
                                borderRadius="md" 
                                border="1px" 
                                borderColor="green.200"
                              >
                                <Text fontSize="sm" whiteSpace="pre-wrap">
                                  {image.extracted_text}
                                </Text>
                              </Box>
                            </Box>
                          )}
                          
                          {image.ai_description && (
                            <Box>
                              <HStack justify="space-between" mb={2}>
                                <Text fontSize="sm" fontWeight="semibold" color="purple.600">
                                  Descrição gerada por IA:
                                </Text>
                                {image.vision_model && (
                                  <Text fontSize="xs" color="gray.500">
                                    {image.vision_model}
                                  </Text>
                                )}
                              </HStack>
                              <Box 
                                p={3} 
                                bg="purple.50" 
                                borderRadius="md" 
                                border="1px" 
                                borderColor="purple.200"
                              >
                                <Text fontSize="sm" whiteSpace="pre-wrap">
                                  {image.ai_description}
                                </Text>
                              </Box>
                            </Box>
                          )}
                          
                          {!image.extracted_text && !image.ai_description && (
                            <Text fontSize="sm" color="gray.500" fontStyle="italic">
                              Nenhum texto ou descrição disponível para esta imagem
                            </Text>
                          )}
                        </VStack>
                        
                        {image.type && (
                          <Text fontSize="xs" color="gray.400" mt={2}>
                            Tipo: {image.type}
                          </Text>
                        )}
                      </Box>
                    ))
                  )}
                </VStack>
              </TabPanel>

            </TabPanels>
          </Tabs>

          <Divider />

          {/* Action Buttons */}
          <HStack justify="space-between">
            <Button variant="outline" onClick={onCancel}>
              Cancelar / Novo Upload
            </Button>
            <Button
              colorScheme="green"
              onClick={onContinue}
              isLoading={isProcessing}
              loadingText="Processando..."
              leftIcon={<FiCheckCircle />}
            >
              Confirmar e Processar
            </Button>
          </HStack>
        </VStack>
      </CardBody>
    </Card>
  );
};

export default DocumentPreview;